import {asyncNoop, sleep} from '@benchling/js-utils';

import AutosaveManager from 'procedures/redux/autosaveManager';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {flowchartConfigEditorUnmounted} from './flowchartConfigActions';
import flowchartConfigAutosaveManagerCallbacks from './flowchartConfigAutosaveManagerCallbacks';

/**
 * This hook is a thin wrapper around the AutosaveManager, customized for workflows flowchart configs. It needs to be
 * used in a component that owns the `save` API call.
 *
 * Its main role is to manage the lifecycle of the AutosaveManager. Not only does it do the normal
 * initialize/destroy, but this hook also manages queuing. E.g., if the user switches to a different
 * tab, then switches back /before the save has completed/, we report that information so downstream
 * components can respond appropriately. (See FlowchartConfigAutosaveManager for more.)
 *
 * Additional Redux-management tools are provided by FlowchartConfigStateManager.
 *
 * Note that callers must install a save handler directly via AutosaveManager.updateSaveHandler.
 */
export default function useFlowchartConfigAutosaveManager(
  flowchartConfigId: string,
  friendlyName: string
): boolean {
  const [hasManagerBeenCreated, setHasManagerBeenCreated] = useStateIfMounted(false);

  useOnMount(() => {
    async function setup(): Promise<void> {
      // We don't have a `doSave` function yet, as this hook is designed to block rendering of the components
      // that can build `doSave`
      await AutosaveManager.initialize(flowchartConfigId, friendlyName, {
        ...flowchartConfigAutosaveManagerCallbacks,
        doSave: asyncNoop,
      });
      setHasManagerBeenCreated(true);
    }
    setup();

    return () => {
      // If the page is unmounted while the flowchart config is dirty, we do a silent "save immediately." Doing so
      // eliminates the need for a "leave before saving?" warning.
      async function cleanup(): Promise<void> {
        // While this sleep isn't strictly necessary, it gives the unmounting page a chance to refresh the
        // save handler installed into the AutosaveManager with empty callbacks. (The callbacks are for
        // bookkeeping, which don't affect the save itself.)
        await sleep(0);
        const isDirty = flowchartConfigAutosaveManagerCallbacks.isDirty(flowchartConfigId);
        if (isDirty) {
          await AutosaveManager.saveImmediately(
            flowchartConfigId,
            () => ({}),
            AutosaveManager.NO_OVERLAY,
            false
          );
        }
        getStore().dispatch(flowchartConfigEditorUnmounted(flowchartConfigId));
        AutosaveManager.destroy(flowchartConfigId);
      }
      cleanup();
    };
  });

  return hasManagerBeenCreated;
}
